import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  bookAgentAppointment,
  fetchAgentAvailability,
  fetchAgentProfile,
} from '../../lib/agentBookingPublicApi';

const PICTON = '#00AAFF';
const POLYNESIAN = '#1B4C87';

const BOOKING_RESIZE_TYPE = 'prosperity-booking-resize';

const PARENT_ORIGIN_ALLOWLIST = [
  'https://prosperityleaders.net',
  'https://www.prosperityleaders.net',
  'https://prosperitywebsite.netlify.app',
];

function isAllowedParentOrigin(origin) {
  if (!origin) return false;
  if (PARENT_ORIGIN_ALLOWLIST.includes(origin)) return true;
  // Deploy-preview / branch deploys for the website Netlify site
  if (/^https:\/\/[a-z0-9-]+--prosperitywebsite\.netlify\.app$/i.test(origin)) return true;
  // Local verification hosts (HTML probe / website dev server)
  if (/^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(origin)) return true;
  return false;
}

function collectParentTargetOrigins() {
  const targets = new Set(PARENT_ORIGIN_ALLOWLIST);
  try {
    if (document.referrer) {
      const refOrigin = new URL(document.referrer).origin;
      if (isAllowedParentOrigin(refOrigin)) targets.add(refOrigin);
    }
  } catch {
    /* ignore bad referrer */
  }
  // Same-origin embed probes (preview HTML served alongside the app)
  try {
    if (isAllowedParentOrigin(window.location.origin)) {
      targets.add(window.location.origin);
    }
  } catch {
    /* ignore */
  }
  return [...targets];
}

function isEmbeddedInIframe() {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin access to window.top can throw in some browsers
    return true;
  }
}

function measureContentHeight(el) {
  if (!el) return 0;
  const rect = el.getBoundingClientRect();
  return Math.ceil(
    Math.max(el.scrollHeight, el.offsetHeight, rect.height || 0),
  );
}

/**
 * When embedded in an allowed parent iframe, report content height via postMessage
 * so the host can auto-resize the frame. No-op for standalone visits.
 */
function useIframeHeightReporter(rootRef, deps = []) {
  const lastPosted = useRef(0);

  const postHeight = useCallback((height) => {
    if (!height || height < 1) return;
    if (height === lastPosted.current) return;
    lastPosted.current = height;
    const payload = { type: BOOKING_RESIZE_TYPE, height };
    for (const origin of collectParentTargetOrigins()) {
      try {
        window.parent.postMessage(payload, origin);
      } catch {
        /* ignore closed/cross-origin parent */
      }
    }
  }, []);

  useEffect(() => {
    if (!isEmbeddedInIframe()) return undefined;

    const el = rootRef.current;
    if (!el) return undefined;

    lastPosted.current = 0;
    el.classList.add('pb-embedded');

    const report = () => {
      postHeight(measureContentHeight(el));
    };

    // Immediate measure (first paint / dep change), then again after layout.
    report();
    const raf = requestAnimationFrame(report);

    const ro = new ResizeObserver(() => {
      requestAnimationFrame(report);
    });
    ro.observe(el);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      el.classList.remove('pb-embedded');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional explicit deps from caller
  }, [postHeight, rootRef, ...deps]);
}

function ymdLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addMonths(date, n) {
  const d = new Date(date.getFullYear(), date.getMonth() + n, 1);
  return d;
}

function monthMatrix(viewDate) {
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const first = new Date(year, month, 1);
  const startPad = first.getDay(); // Sun=0
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function formatSlotLabel(iso) {
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatConfirmWhen(iso, durationMinutes) {
  try {
    const start = new Date(iso);
    const date = start.toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
    const time = start.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
    return `${date} at ${time} (${durationMinutes} min)`;
  } catch {
    return iso;
  }
}

const emptyForm = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  notes: '',
};

export default function PublicBooking() {
  const { slug } = useParams();
  const rootRef = useRef(null);
  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(null);
  const [profile, setProfile] = useState(null);

  const [viewMonth, setViewMonth] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState(() => ymdLocal(today));
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [timezone, setTimezone] = useState('America/New_York');
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [googleConnected, setGoogleConnected] = useState(true);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [form, setForm] = useState(emptyForm);
  const [guests, setGuests] = useState([]);
  const [guestFormOpen, setGuestFormOpen] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestFormError, setGuestFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [done, setDone] = useState(null);

  // Re-attach observer across loading → calendar → confirmation trees, and
  // re-measure when availability finishes loading (calendar height depends on it).
  useIframeHeightReporter(rootRef, [
    loading,
    notFound,
    done,
    slotsLoading,
    slots.length,
    selectedSlot,
    submitError,
    viewMonth,
    guests.length,
    guestFormOpen,
  ]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setNotFound(false);
      const result = await fetchAgentProfile(slug);
      if (cancelled) return;
      if (!result.ok) {
        if (result.notFound) setNotFound(true);
        else setError(result.error || 'Failed to load profile');
        setProfile(null);
      } else {
        const p = result.data?.profile || null;
        setProfile(p);
        if (p?.booking?.calendar_mode && p.booking.calendar_mode !== 'crm') {
          setError('This agent is not taking bookings through the CRM calendar.');
        }
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (!slug || !profile) return;
    const mode = profile?.booking?.calendar_mode || 'crm';
    if (mode !== 'crm') return;
    let cancelled = false;
    (async () => {
      setSlotsLoading(true);
      setSelectedSlot(null);
      const start = ymdLocal(new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1));
      const end = ymdLocal(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0));
      const result = await fetchAgentAvailability(slug, start, end);
      if (cancelled) return;
      if (!result.ok && result.notFound) {
        setNotFound(true);
        setSlots([]);
      } else {
        setSlots(result.slots || []);
        if (result.timezone) setTimezone(result.timezone);
        if (result.duration_minutes) setDurationMinutes(result.duration_minutes);
        setGoogleConnected(result.google_connected !== false);
      }
      setSlotsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, profile, viewMonth]);

  const daysWithSlots = useMemo(() => {
    const set = new Set();
    for (const s of slots) {
      if (!s?.start) continue;
      const d = new Date(s.start);
      set.add(ymdLocal(d));
    }
    return set;
  }, [slots]);

  const daySlots = useMemo(() => {
    return slots.filter((s) => s?.start && ymdLocal(new Date(s.start)) === selectedDate);
  }, [slots, selectedDate]);

  const monthLabel = viewMonth.toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });

  const addGuest = () => {
    const name = guestName.trim();
    const email = guestEmail.trim().toLowerCase();
    if (!name) {
      setGuestFormError('Guest name is required');
      return;
    }
    if (!email || !email.includes('@')) {
      setGuestFormError('Guest email is required');
      return;
    }
    if (guests.some((g) => g.email === email)) {
      setGuestFormError('That guest is already added');
      return;
    }
    if (guests.length >= 5) {
      setGuestFormError('You can add up to 5 guests');
      return;
    }
    setGuests((prev) => [...prev, { type: 'external', name, email }]);
    setGuestName('');
    setGuestEmail('');
    setGuestFormError(null);
    setGuestFormOpen(false);
  };

  const removeGuest = (index) => {
    setGuests((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSlot || !slug) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await bookAgentAppointment({
        slug,
        scheduled_at: selectedSlot,
        duration_minutes: durationMinutes,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        notes: form.notes.trim() || undefined,
        ...(guests.length > 0 ? { guests } : {}),
      });
      setDone({
        scheduled_at: result.scheduled_at || selectedSlot,
        duration_minutes: result.duration_minutes || durationMinutes,
        agent_name: profile?.full_name || 'your advisor',
      });
    } catch (err) {
      setSubmitError(err?.message || 'Booking failed. Please try another time.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="pb-root" ref={rootRef}>
        <div className="pb-shell pb-center">
          <div className="pb-spinner" aria-hidden />
          <p>Loading booking page…</p>
        </div>
        <StyleTag />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="pb-root" ref={rootRef}>
        <div className="pb-shell pb-center">
          <h1 className="pb-brand">Prosperity Leaders</h1>
          <p className="pb-muted">This booking page is unavailable.</p>
        </div>
        <StyleTag />
      </div>
    );
  }

  if (done) {
    return (
      <div className="pb-root" ref={rootRef}>
        <div className="pb-shell pb-center pb-confirm">
          <p className="pb-brand">Prosperity Leaders</p>
          <h1>You&apos;re booked</h1>
          <p>
            Your call with <strong>{done.agent_name}</strong> is confirmed for{' '}
            {formatConfirmWhen(done.scheduled_at, done.duration_minutes)}.
          </p>
          <p className="pb-muted">A confirmation email is on its way.</p>
        </div>
        <StyleTag />
      </div>
    );
  }

  const calendarMode = profile?.booking?.calendar_mode || 'crm';
  const canBook = calendarMode === 'crm';

  return (
    <div className="pb-root" ref={rootRef}>
      <div className="pb-shell">
        <div className="pb-layout">
          <aside className="pb-sidebar">
            <header className="pb-hero">
              <p className="pb-brand">Prosperity Leaders</p>
              <div className="pb-agent">
                {profile?.headshot_url ? (
                  <img
                    src={profile.headshot_url}
                    alt=""
                    width={72}
                    height={72}
                    loading="lazy"
                    decoding="async"
                    className="pb-avatar"
                  />
                ) : (
                  <div className="pb-avatar pb-avatar-fallback" aria-hidden>
                    {(profile?.full_name || '?').slice(0, 1)}
                  </div>
                )}
                <div>
                  <h1>{profile?.full_name || 'Book a call'}</h1>
                  {profile?.title ? <p className="pb-title">{profile.title}</p> : null}
                  <p className="pb-lede">
                    {profile?.booking?.cta_text || 'Pick a time that works for you.'}
                  </p>
                </div>
              </div>
            </header>
          </aside>

          <div className="pb-main">
            {error ? <div className="pb-banner">{error}</div> : null}

            {canBook ? (
              <div className="pb-grid">
                <section className="pb-panel pb-cal-panel" aria-label="Choose a date">
                  <div className="pb-month-nav">
                    <button
                      type="button"
                      className="pb-nav-btn"
                      onClick={() => setViewMonth((m) => addMonths(m, -1))}
                      disabled={
                        viewMonth.getFullYear() === today.getFullYear() &&
                        viewMonth.getMonth() === today.getMonth()
                      }
                    >
                      ‹
                    </button>
                    <h2>{monthLabel}</h2>
                    <button
                      type="button"
                      className="pb-nav-btn"
                      onClick={() => setViewMonth((m) => addMonths(m, 1))}
                    >
                      ›
                    </button>
                  </div>
                  <div className="pb-dow">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                      <span key={d}>{d}</span>
                    ))}
                  </div>
                  <div className="pb-cal">
                    {monthMatrix(viewMonth).map((cell, idx) => {
                      if (!cell) return <span key={`e-${idx}`} className="pb-day empty" />;
                      const ymd = ymdLocal(cell);
                      const isPast = cell < today;
                      const has = daysWithSlots.has(ymd);
                      const selected = ymd === selectedDate;
                      return (
                        <button
                          key={ymd}
                          type="button"
                          disabled={isPast || (!has && !slotsLoading)}
                          className={[
                            'pb-day',
                            selected ? 'selected' : '',
                            has ? 'has-slots' : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          onClick={() => {
                            setSelectedDate(ymd);
                            setSelectedSlot(null);
                          }}
                        >
                          {cell.getDate()}
                        </button>
                      );
                    })}
                  </div>
                  <p className="pb-tz">Times in {timezone.replace(/_/g, ' ')}</p>
                </section>

                <section className="pb-panel pb-times-panel" aria-label="Choose a time">
                  <h2>Available times</h2>
                  {!googleConnected ? (
                    <p className="pb-muted">No open times right now. Check back soon.</p>
                  ) : slotsLoading ? (
                    <p className="pb-muted">Loading times…</p>
                  ) : daySlots.length === 0 ? (
                    <p className="pb-muted">No open slots on this day.</p>
                  ) : (
                    <div className="pb-slots">
                      {daySlots.map((s) => (
                        <button
                          key={s.start}
                          type="button"
                          className={selectedSlot === s.start ? 'pb-slot selected' : 'pb-slot'}
                          onClick={() => setSelectedSlot(s.start)}
                        >
                          {formatSlotLabel(s.start)}
                        </button>
                      ))}
                    </div>
                  )}
                </section>

                <section className="pb-panel pb-form-panel" aria-label="Your details">
                  <h2>Your details</h2>
                  <form onSubmit={onSubmit} className="pb-form">
                    <div className="pb-row">
                      <label>
                        First name
                        <input
                          required
                          value={form.first_name}
                          onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                          disabled={submitting}
                        />
                      </label>
                      <label>
                        Last name
                        <input
                          required
                          value={form.last_name}
                          onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                          disabled={submitting}
                        />
                      </label>
                    </div>
                    <label>
                      Email
                      <input
                        required
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                        disabled={submitting}
                      />
                    </label>
                    <label>
                      Phone
                      <input
                        required
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                        disabled={submitting}
                      />
                    </label>
                    <label>
                      Notes <span className="pb-optional">(optional)</span>
                      <textarea
                        rows={3}
                        value={form.notes}
                        onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                        disabled={submitting}
                      />
                    </label>

                    <div className="pb-guests">
                      <div className="pb-guests-head">
                        <span>Guests</span>
                        <span className="pb-optional">optional · up to 5</span>
                      </div>
                      {guests.length > 0 ? (
                        <ul className="pb-guest-list">
                          {guests.map((g, index) => (
                            <li key={`${g.email}-${index}`}>
                              <div>
                                <strong>{g.name}</strong>
                                <span>{g.email}</span>
                              </div>
                              <button
                                type="button"
                                className="pb-guest-remove"
                                disabled={submitting}
                                onClick={() => removeGuest(index)}
                                aria-label={`Remove ${g.name}`}
                              >
                                ×
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                      {!guestFormOpen ? (
                        <button
                          type="button"
                          className="pb-guest-add"
                          disabled={submitting || guests.length >= 5}
                          onClick={() => {
                            setGuestFormOpen(true);
                            setGuestFormError(null);
                          }}
                        >
                          ＋ Add a guest
                        </button>
                      ) : (
                        <div className="pb-guest-form">
                          <div className="pb-row">
                            <label>
                              Guest name
                              <input
                                value={guestName}
                                onChange={(e) => setGuestName(e.target.value)}
                                disabled={submitting}
                                placeholder="Full name"
                              />
                            </label>
                            <label>
                              Guest email
                              <input
                                type="email"
                                value={guestEmail}
                                onChange={(e) => setGuestEmail(e.target.value)}
                                disabled={submitting}
                                placeholder="email@example.com"
                              />
                            </label>
                          </div>
                          {guestFormError ? <p className="pb-err">{guestFormError}</p> : null}
                          <div className="pb-guest-actions">
                            <button
                              type="button"
                              className="pb-guest-save"
                              disabled={submitting}
                              onClick={addGuest}
                            >
                              Add guest
                            </button>
                            <button
                              type="button"
                              className="pb-guest-cancel"
                              disabled={submitting}
                              onClick={() => {
                                setGuestFormOpen(false);
                                setGuestName('');
                                setGuestEmail('');
                                setGuestFormError(null);
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {submitError ? <p className="pb-err">{submitError}</p> : null}
                    <button
                      type="submit"
                      className="pb-submit"
                      disabled={submitting || !selectedSlot}
                    >
                      {submitting ? 'Booking…' : selectedSlot ? 'Confirm booking' : 'Select a time'}
                    </button>
                  </form>
                </section>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <StyleTag />
    </div>
  );
}

function StyleTag() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,600;9..144,700&family=Manrope:wght@400;500;600;700&display=swap');
      .pb-root {
        --pb-ink: ${POLYNESIAN};
        --pb-accent: ${PICTON};
        --pb-paper: #f7f4ef;
        --pb-line: rgba(27, 76, 135, 0.14);
        /* Query this element's own width so iframe embeds respond to the
           frame size — not the parent page's viewport. */
        container-type: inline-size;
        container-name: pb;
        min-height: 100vh;
        background:
          radial-gradient(1200px 600px at 10% -10%, rgba(0,170,255,0.18), transparent 55%),
          radial-gradient(900px 500px at 100% 0%, rgba(27,76,135,0.16), transparent 50%),
          linear-gradient(180deg, #fbfaf7 0%, var(--pb-paper) 100%);
        color: var(--pb-ink);
        font-family: 'Manrope', system-ui, sans-serif;
      }
      /* Drop viewport-filling min-height inside embeds so reported height
         tracks real content instead of the iframe's current client height. */
      .pb-root.pb-embedded {
        min-height: auto;
      }
      .pb-shell {
        max-width: 1152px;
        margin: 0 auto;
        padding: 2.5rem 1.25rem 4rem;
      }
      .pb-layout {
        display: flex;
        flex-direction: column;
        gap: 0;
      }
      .pb-main { min-width: 0; }
      .pb-center {
        min-height: 70vh;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        gap: 0.75rem;
      }
      .pb-brand {
        font-family: 'Fraunces', Georgia, serif;
        font-size: 1.15rem;
        letter-spacing: 0.02em;
        color: var(--pb-ink);
        margin: 0 0 1rem;
      }
      .pb-hero h1, .pb-confirm h1 {
        font-family: 'Fraunces', Georgia, serif;
        font-size: clamp(1.75rem, 4vw, 2.35rem);
        margin: 0.15rem 0;
        line-height: 1.15;
      }
      .pb-agent {
        display: flex;
        gap: 1.1rem;
        align-items: center;
      }
      .pb-avatar {
        width: 72px;
        height: 72px;
        border-radius: 50%;
        object-fit: cover;
        border: 3px solid white;
        box-shadow: 0 8px 24px rgba(27,76,135,0.18);
        flex-shrink: 0;
      }
      .pb-avatar-fallback {
        display: grid;
        place-items: center;
        background: var(--pb-accent);
        color: white;
        font-family: 'Fraunces', Georgia, serif;
        font-size: 1.6rem;
      }
      .pb-title { color: var(--pb-accent); font-weight: 600; margin: 0; }
      .pb-lede { margin: 0.35rem 0 0; color: rgba(27,76,135,0.78); max-width: 36rem; }
      .pb-muted { color: rgba(27,76,135,0.65); }
      .pb-banner {
        margin: 1rem 0 0;
        padding: 0.85rem 1rem;
        border-radius: 10px;
        background: rgba(180, 50, 50, 0.08);
        border: 1px solid rgba(180, 50, 50, 0.2);
        color: #8a2a2a;
      }
      .pb-grid {
        display: grid;
        gap: 1rem;
        margin-top: 1.75rem;
        grid-template-columns: 1fr;
        align-items: stretch;
      }
      /* Mid-width embed / tablet: calendar | times side-by-side, form full width.
         Uses container queries so iframe width (not parent viewport) drives this.
         ~640px fits compact calendar (~22.75rem) + times column in embeds. */
      @container pb (min-width: 560px) {
        .pb-row { grid-template-columns: 1fr 1fr; }
      }
      @container pb (min-width: 640px) {
        .pb-grid {
          grid-template-columns: minmax(0, 22.75rem) minmax(0, 1fr);
        }
        .pb-form-panel { grid-column: 1 / -1; }
        .pb-cal-panel,
        .pb-times-panel {
          height: 100%;
        }
        .pb-times-panel {
          display: flex;
          flex-direction: column;
          min-width: 0;
        }
        .pb-times-panel .pb-slots {
          flex: 1 1 auto;
          align-content: start;
          overflow-y: auto;
        }
      }
      /* Wide standalone: agent identity sidebar + booking column.
         Calendar | times row stays active (from 640px rule); only layout chrome changes. */
      @container pb (min-width: 980px) {
        .pb-layout {
          display: grid;
          grid-template-columns: minmax(220px, 0.36fr) minmax(0, 1fr);
          gap: 2.5rem;
          align-items: start;
        }
        .pb-sidebar {
          position: sticky;
          top: 1.5rem;
        }
        .pb-agent {
          flex-direction: column;
          align-items: flex-start;
          text-align: left;
          gap: 1.25rem;
        }
        .pb-avatar {
          width: 96px;
          height: 96px;
        }
        .pb-lede { max-width: none; }
        .pb-grid { margin-top: 0; }
        .pb-banner { margin-top: 0; margin-bottom: 1rem; }
      }
      .pb-panel {
        background: rgba(255,255,255,0.72);
        border: 1px solid var(--pb-line);
        border-radius: 16px;
        padding: 1.15rem 1.2rem 1.35rem;
        backdrop-filter: blur(6px);
      }
      /* Compact calendar: ~45–52px day cells, no wide empty gutters */
      .pb-cal-panel {
        width: 100%;
        max-width: 22.75rem;
        padding: 0.7rem 0.75rem 0.85rem;
        border-radius: 12px;
        box-sizing: border-box;
      }
      .pb-panel h2 {
        font-family: 'Fraunces', Georgia, serif;
        font-size: 1.15rem;
        margin: 0 0 0.85rem;
      }
      .pb-month-nav {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.35rem;
        margin-bottom: 0.4rem;
      }
      .pb-month-nav h2 {
        margin: 0;
        font-size: 0.95rem;
        font-weight: 700;
        line-height: 1.2;
      }
      .pb-nav-btn {
        width: 28px; height: 28px; border-radius: 999px;
        border: 1px solid var(--pb-line); background: white;
        color: var(--pb-ink); cursor: pointer; font-size: 1rem;
        line-height: 1; flex-shrink: 0;
        display: grid; place-items: center; padding: 0;
      }
      .pb-nav-btn:disabled { opacity: 0.35; cursor: default; }
      .pb-dow, .pb-cal {
        display: grid;
        grid-template-columns: repeat(7, minmax(0, 1fr));
        gap: 0.2rem;
        width: 100%;
      }
      .pb-dow span {
        text-align: center;
        font-size: 0.58rem;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: rgba(27,76,135,0.55);
        padding-bottom: 0.15rem;
      }
      .pb-day {
        aspect-ratio: 1;
        min-height: 2.5rem;
        border-radius: 7px;
        border: 1px solid transparent;
        background: transparent;
        color: var(--pb-ink);
        cursor: pointer;
        font-weight: 600;
        font-size: 0.8rem;
        line-height: 1;
        padding: 0;
        display: grid;
        place-items: center;
      }
      .pb-day.empty { visibility: hidden; }
      .pb-day:disabled { opacity: 0.28; cursor: default; }
      .pb-day.has-slots { background: rgba(0,170,255,0.08); }
      .pb-day.selected {
        background: var(--pb-ink);
        color: white;
        border-color: var(--pb-ink);
      }
      .pb-tz { margin: 0.5rem 0 0; font-size: 0.75rem; color: rgba(27,76,135,0.55); }
      .pb-slots {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
        gap: 0.5rem;
      }
      .pb-slot {
        border: 1px solid var(--pb-line);
        background: white;
        border-radius: 10px;
        padding: 0.65rem 0.4rem;
        cursor: pointer;
        font-weight: 600;
        color: var(--pb-ink);
      }
      .pb-slot.selected {
        background: var(--pb-accent);
        border-color: var(--pb-accent);
        color: white;
      }
      .pb-form { display: grid; gap: 0.85rem; }
      .pb-row { display: grid; gap: 0.85rem; }
      .pb-form label {
        display: grid; gap: 0.35rem;
        font-size: 0.85rem; font-weight: 600;
      }
      .pb-form input, .pb-form textarea {
        width: 100%;
        border: 1px solid var(--pb-line);
        border-radius: 10px;
        padding: 0.7rem 0.8rem;
        font: inherit;
        color: var(--pb-ink);
        background: white;
      }
      .pb-form input:focus, .pb-form textarea:focus {
        outline: 2px solid rgba(0,170,255,0.35);
        border-color: var(--pb-accent);
      }
      .pb-optional { font-weight: 500; color: rgba(27,76,135,0.5); }
      .pb-guests {
        display: grid;
        gap: 0.65rem;
        padding-top: 0.15rem;
      }
      .pb-guests-head {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 0.75rem;
        font-size: 0.85rem;
        font-weight: 600;
      }
      .pb-guest-list {
        list-style: none;
        margin: 0;
        padding: 0;
        display: grid;
        gap: 0.4rem;
      }
      .pb-guest-list li {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.75rem;
        padding: 0.55rem 0.7rem;
        border: 1px solid var(--pb-line);
        border-radius: 10px;
        background: white;
      }
      .pb-guest-list li div {
        display: grid;
        gap: 0.1rem;
        min-width: 0;
      }
      .pb-guest-list li strong {
        font-size: 0.9rem;
      }
      .pb-guest-list li span {
        font-size: 0.78rem;
        color: rgba(27,76,135,0.65);
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .pb-guest-remove {
        border: none;
        background: transparent;
        color: rgba(27,76,135,0.45);
        font-size: 1.2rem;
        line-height: 1;
        cursor: pointer;
        padding: 0.15rem 0.35rem;
      }
      .pb-guest-remove:hover { color: var(--pb-ink); }
      .pb-guest-add {
        justify-self: start;
        border: 1px dashed var(--pb-line);
        background: rgba(255,255,255,0.7);
        color: var(--pb-ink);
        border-radius: 10px;
        padding: 0.55rem 0.85rem;
        font: inherit;
        font-weight: 600;
        font-size: 0.88rem;
        cursor: pointer;
      }
      .pb-guest-add:disabled { opacity: 0.45; cursor: default; }
      .pb-guest-form {
        display: grid;
        gap: 0.65rem;
        padding: 0.75rem;
        border: 1px solid var(--pb-line);
        border-radius: 12px;
        background: rgba(255,255,255,0.85);
      }
      .pb-guest-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 0.65rem;
        align-items: center;
      }
      .pb-guest-save {
        border: none;
        border-radius: 10px;
        padding: 0.55rem 0.9rem;
        background: var(--pb-ink);
        color: white;
        font: inherit;
        font-weight: 600;
        font-size: 0.88rem;
        cursor: pointer;
      }
      .pb-guest-cancel {
        border: none;
        background: transparent;
        color: rgba(27,76,135,0.65);
        font: inherit;
        font-size: 0.88rem;
        cursor: pointer;
        padding: 0.35rem 0.15rem;
      }
      .pb-err { color: #8a2a2a; margin: 0; font-size: 0.9rem; }
      .pb-submit {
        margin-top: 0.25rem;
        border: none;
        border-radius: 12px;
        padding: 0.9rem 1.2rem;
        background: var(--pb-accent);
        color: white;
        font-weight: 700;
        font-size: 1rem;
        cursor: pointer;
      }
      .pb-submit:disabled { opacity: 0.45; cursor: default; }
      .pb-spinner {
        width: 2rem; height: 2rem; border-radius: 999px;
        border: 2px solid rgba(27,76,135,0.2);
        border-top-color: var(--pb-accent);
        animation: pbspin 0.8s linear infinite;
      }
      @keyframes pbspin { to { transform: rotate(360deg); } }
      .pb-confirm strong { color: var(--pb-accent); }
    `}</style>
  );
}
